import { useOktaAuth } from '@okta/okta-react'
import { legit } from 'apex-legit-service'
import { ApexApiError } from 'apex-ui-services/src/types/CommonTypes'
import React, { useEffect } from 'react'
import { connect } from 'react-redux'

import { RootState } from '../../store'
import { LoginPage } from './Login'
import {
  AccessToken,
  IDToken,
  SessionObject,
  TokenResponse,
} from '@okta/okta-auth-js'
import {
  ApexAuth,
  LoginTypePayload,
  LoginTypeResponse,
  SsoTokenPayload,
} from 'apex-ui-services'
import {
  ALL_REQUESTED_PERMISSIONS,
  LEGIT_SERVICE_CONFIG,
} from '../../constants'

export interface Props {
  idpCheck: (params: LoginTypePayload) => void
  loginTypeResponse?: LoginTypeResponse | null
  ssoLogin: (params: SsoTokenPayload) => void
  isLegacyLoginLoading: boolean
  ssoLoginIsLoading: boolean
  isLoginTypeLoading: boolean
  adoptLegitSession: (
    config: Record<string, any>,
    requestedPermissions: string[]
  ) => void
  failedLegitAuth: boolean
  ssoError?: ApexApiError | null
  clearSsoLogin: () => void
  isSessionLoading: boolean
}

export const ApexLoginComponent = ({
  ssoLoginIsLoading,
  adoptLegitSession,
  ssoLogin,
  failedLegitAuth,
  ssoError,
  clearSsoLogin,
  isSessionLoading,
}: Props) => {
  // eslint-disable-next-line
  const { authState, oktaAuth } = useOktaAuth()
  const retrieveOktaSession = async () => {
    const session: SessionObject = await oktaAuth.session.get()
    if (session.status === 'ACTIVE') {
      let token: TokenResponse
      try {
        token = await oktaAuth.token.getWithoutPrompt()
      } catch (e) {
        token = await oktaAuth.token.parseFromUrl()
      }
      const idToken: IDToken | undefined = token?.tokens?.idToken
      const accessToken: AccessToken | undefined = token?.tokens?.accessToken
      ssoLogin({
        idToken: idToken?.idToken,
        accessToken: accessToken?.accessToken,
      })
    }
  }

  useEffect(() => {
    if (failedLegitAuth && !ssoError) {
      retrieveOktaSession()
    }
    // eslint-disable-next-line
  }, [failedLegitAuth])

  useEffect(() => {
    if (authState?.isAuthenticated) {
      const idToken: IDToken = authState?.idToken
      const accessToken: AccessToken = authState?.accessToken
      ssoLogin({
        idToken: idToken?.idToken,
        accessToken: accessToken?.accessToken,
      })
    }
    // eslint-disable-next-line
  }, [authState])

  adoptLegitSession(LEGIT_SERVICE_CONFIG, ALL_REQUESTED_PERMISSIONS)

  return <LoginPage isLoadingSession={isSessionLoading} />
}

const mapStateToProps = (state: RootState) => {
  const legitAuthState = (state as any)?.APEX_LEGIT_AUTHORIZATION?.status
  return {
    isLegacyLoginLoading: legit.selectors.isSessionLoading(state),
    loginTypeResponse: ApexAuth.loginType.selectors.loginType(state),
    isLoginTypeLoading: ApexAuth.loginType.selectors.isLoginTypeLoading(state),
    ssoLoginIsLoading: ApexAuth.loginSso.selectors.isSsoLoginLoading(state),
    ssoError: ApexAuth.loginSso.selectors.error(state),
    failedLegitAuth: legitAuthState === 'APEX_LEGIT_AUTHORIZATION/FAILURE',
  }
}

const mapDispatchToProps = {
  adoptLegitSession: legit.adoptExistingSession,
  idpCheck: ApexAuth.loginType.actions.getLoginType.request,
  ssoLogin: ApexAuth.loginSso.actions.createSsoLogin.request,
  clearSsoLogin: ApexAuth.loginSso.actions.clearSsoLogin,
}

export const ApexLogin = connect(
  mapStateToProps,
  mapDispatchToProps
)(ApexLoginComponent)
